import { css } from 'linaria';
import { Fragment, h } from 'preact';
import { styled } from 'linaria/react';
import { useStore } from 'effector-react';
import { useEffect, useState } from 'preact/hooks';

import { Image } from 'ui/image';
import { Camera } from 'ui/camera';
import { Footer } from 'ui/footer';
import { Text } from 'ui/typography';
import { isBlob } from 'libs/is-blob';
import { Button } from 'ui/atoms/button';
import { Container } from 'ui/container';
import { ErrorIcon } from 'ui/icons/error-icon';
import { Photo, PhotoStep } from 'model/domain';
import { photoErrorsMap } from 'model/error.model';
import { Header } from 'features/verify/ui/header';
import { InputCapture } from 'ui/input/input-capture';
import { sendEvent } from 'model/helpers/events.model';
import { useLocalization } from 'model/localization.model';
import { ButtonContainer } from 'features/verify/ui/button-container';
import { screenHistoryPush, ScreenName } from 'model/helpers/screens-history';
import { EnlargeButton, EnlargePhoto } from 'features/verify/ui/enlarge-photo';
import { errors$, MAX_ATTEMPTS, photo$, step$, uploadAttempts$, uploadPhotoFx } from 'model/flows/verification.model';

export const InvalidPhotoError = () => {
	const t = useLocalization();
	const step = useStore(step$);
	const photo = useStore(photo$);
	const errors = useStore(errors$);
	const attempts = useStore(uploadAttempts$);
	const [enlarged, updateEnlarged] = useState(false);
	const [imageSource, updateImageSource] = useState(null);

	const CameraComponent = step === PhotoStep.selfie ?
		Camera :
		InputCapture;
	const cameraType = step === PhotoStep.selfie ? 'front' : 'back';
	const canContinue = attempts >= MAX_ATTEMPTS && (step !== PhotoStep.front_side || !errors.includes('document_not_supported') || !errors.indexOf('document_missing'));
	const photoType = step === PhotoStep.front_side ?
		'doc_front' : step === PhotoStep.back_side ?
			'doc_back' : 'selfie';

	useEffect(() => {
		if (isBlob(photo)) {
			const urlCreator = window.URL || window.webkitURL;
			updateImageSource(urlCreator.createObjectURL(photo));
		} else {
			updateImageSource(photo);
		}
	}, [photo]);
	useEffect(() => {
		switch (step) {
			case PhotoStep.front_side:
				screenHistoryPush(ScreenName.doc_front_feedback);
				break;
			case PhotoStep.back_side:
				screenHistoryPush(ScreenName.doc_back_feedback);
				break;
			case PhotoStep.selfie:
				screenHistoryPush(ScreenName.selfie_feedback);
				break;
		}
		setTimeout(() => {
			sendEvent({
				event: `${photoType}_feedback_screen_shown` as any,
				event_attributes: {
					can_continue: canContinue,
					error_code: errors,
				},
			});
		}, 20);
	}, []);

	const handleUpload = async (photo: Photo, caution?: 'false' | 'true') => {
		const t0 = performance.now();
		await uploadPhotoFx({ photo, caution });
		const upload_time = Math.round(t0 - performance.now());
		sendEvent({
			event: `${photoType}_upload_success` as any,
			event_attributes: { upload_time },
		});
	};
	const handleRetry = (photo: Photo) => {
		sendEvent({
			event: `${photoType}_feedback_retry` as any,
			event_attributes: { can_continue: canContinue },
		});
		handleUpload(photo);
	};
	const handleContinue = () => {
		sendEvent({
			event: `${photoType}_feedback_continue` as any,
			event_attributes: { can_continue: canContinue },
		});
		handleUpload(photo, 'true');
	};

	return (
		<Fragment>
			<Header
				extra={
					<HeaderTitle>
						{t('mobile.resubmission.title')}
					</HeaderTitle>
				}
			/>
			<Container style={{ paddingBottom: 8 }}>
				<Text>
					{t('mobile.resubmission.subtitle')}
				</Text>
				<ImageWrapper>
					<ImageContainer>
						{imageSource !== null && (
							<Image
								className={photoImage}
								src={imageSource}
							/>
						)}
					</ImageContainer>
					<EnlargeButton
						onClick={() => updateEnlarged(true)}
						style={{
							position: 'absolute',
							bottom: 8,
							left: '50%',
							transform: 'translateX(-50%)',
						}}
					>
						<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M1 5.96429V2.64286C1 2.28661 1.28661 2 1.64286 2H4.96429C5.14107 2 5.28571 2.14464 5.28571 2.32143V2.96429C5.28571 3.14107 5.14107 3.28571 4.96429 3.28571H2.28571V5.96429C2.28571 6.14107 2.14107 6.28571 1.96429 6.28571H1.32143C1.14464 6.28571 1 6.14107 1 5.96429ZM8.71429 2.32143V2.96429C8.71429 3.14107 8.85893 3.28571 9.03571 3.28571H11.7143V5.96429C11.7143 6.14107 11.8589 6.28571 12.0357 6.28571H12.6786C12.8554 6.28571 13 6.14107 13 5.96429V2.64286C13 2.28661 12.7134 2 12.3571 2H9.03571C8.85893 2 8.71429 2.14464 8.71429 2.32143ZM12.6786 9.71429H12.0357C11.8589 9.71429 11.7143 9.85893 11.7143 10.0357V12.7143H9.03571C8.85893 12.7143 8.71429 12.8589 8.71429 13.0357V13.6786C8.71429 13.8554 8.85893 14 9.03571 14H12.3571C12.7134 14 13 13.7134 13 13.3571V10.0357C13 9.85893 12.8554 9.71429 12.6786 9.71429ZM5.28571 13.6786V13.0357C5.28571 12.8589 5.14107 12.7143 4.96429 12.7143H2.28571V10.0357C2.28571 9.85893 2.14107 9.71429 1.96429 9.71429H1.32143C1.14464 9.71429 1 9.85893 1 10.0357V13.3571C1 13.7134 1.28661 14 1.64286 14H4.96429C5.14107 14 5.28571 13.8554 5.28571 13.6786Z"
								fill="white"/>
						</svg>
						{t('mobile.resubmission.zoomin')}
					</EnlargeButton>
				</ImageWrapper>
				<ErrorBlock>
					<IconContainer>
						<ErrorIcon/>
					</IconContainer>
					<div>
						{errors.map((code, index) => (
							<p key={index}>{t(photoErrorsMap[code])}</p>
						))}
					</div>
				</ErrorBlock>
				{canContinue ? (
					<ButtonContainer>
						<CameraComponent
							className={fluidClass}
							cameraType={cameraType}
							onCapture={handleRetry}
						>
							<Button
								name={'mobile.resubmission.btn'}
								className={btn}
								type={'default'}
							>
								{t('mobile.resubmission.btn')}
							</Button>
						</CameraComponent>
						<Button
							onClick={handleContinue}
							className={fluidClass}
							name={'mobile.resubmission.btn.proceed'}
						>
							{t('mobile.resubmission.btn.proceed')}
						</Button>
					</ButtonContainer>
				) : (
					<ButtonContainer>
						<CameraComponent
							className={fluidClass}
							cameraType={cameraType}
							onCapture={handleRetry}
						>
							<Button
								className={fluidClass}
								name={'mobile.resubmission.btn'}
							>
								{t('mobile.resubmission.btn')}
							</Button>
						</CameraComponent>
					</ButtonContainer>
				)}
			</Container>
			<Footer/>
			{enlarged && (
				<EnlargePhoto
					photo={photo}
					onClose={() => updateEnlarged(false)}
				/>
			)}
		</Fragment>
	);
};

const btn = css`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-primary);
  width: 100%;
`;
const photoImage = css`
  object-fit: contain;
  width: 100%;
  max-height: 100%;
  top: 0;
`;
const ImageWrapper = styled('div')`
  position: relative;
  min-height: 40vh;
  max-height: 40vh;
  display: flex;
  margin-top: 8px;
  margin-bottom: 8px;
  background: transparent !important;

  @media (max-height: 1500px) {
    min-height: 33vh;
    max-height: 33vh;
  }

  @media (max-height: 500px) {
    min-height: 25vh;
    max-height: 25vh;
  }
`;
const ImageContainer = styled('div')`
  flex: 1;
  display: flex;
  border-radius: 4px;
  overflow: hidden;
  min-height: 40vh;

  @media (max-height: 1500px) {
    min-height: 33vh;
    max-height: 33vh;
  }

  @media (max-height: 500px) {
    min-height: 25vh;
    max-height: 25vh;
  }
`;

const fluidClass = css`
  width: 100%;
`;
const IconContainer = styled('div')`
  display: flex;
  align-items: center;
  height: 20px;
  margin-right: 8px;
`;
const ErrorBlock = styled('div')`
  display: flex;
  padding: 10px 8px;
  border-radius: 4px;
  border: 1px solid var(--error-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: var(--text-primary);

  p {
    margin-top: 0;
    margin-bottom: 2px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const HeaderTitle = styled('p')`
  margin: 0;
  padding: 0;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-secondary);
`;
